import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, Button, StatusChip } from "@fragments";
import {
    IconPlus,
    IconRefresh,
    IconSquareRoundedMinus,
    IconAlertTriangle,
    IconChecks,
} from "@tabler/icons-react";
import * as Mui from "@mui/material";

// The keys on the left are API document types / statuses and stay untranslated;
// the copy is resolved from the caller's `t` so it follows the active language.
const getDocsLabels = (t) => ({
    ALLOW_BANK_ACCOUNT_DEPOSIT_STATEMENT: t("onboarding.documents.types.allowBankAccountDepositStatement"),
    CUSTOM: t("onboarding.documents.types.custom"),
    EMANCIPATION_OF_MINORS: t("onboarding.documents.types.emancipationOfMinors"),
    ENTREPRENEUR_REQUIREMENT: t("onboarding.documents.types.entrepreneurRequirement"),
    IDENTIFICATION_SELFIE: t("onboarding.documents.types.identificationSelfie"),
    IDENTIFICATION: t("onboarding.documents.types.identification"),
    INVOICE: t("onboarding.documents.types.invoice"),
    MEI_CERTIFICATE: t("onboarding.documents.types.meiCertificate"),
    MINUTES_OF_CONSTITUTION: t("onboarding.documents.types.minutesOfConstitution"),
    MINUTES_OF_ELECTION: t("onboarding.documents.types.minutesOfElection"),
    POWER_OF_ATTORNEY: t("onboarding.documents.types.powerOfAttorney"),
    SOCIAL_CONTRACT: t("onboarding.documents.types.socialContract")
});

const getStatusConfig = (t) => ({
    pending: { text: t("onboarding.documents.statuses.pending"), variant: "default", Icon: IconSquareRoundedMinus },
    rejected: { text: t("onboarding.documents.statuses.rejected"), variant: "error", Icon: IconAlertTriangle },
    completed: { text: t("onboarding.documents.statuses.completed"), variant: "success", Icon: IconChecks },
    approved: { text: t("onboarding.documents.statuses.approved"), variant: "success", Icon: IconChecks },
    awaiting_approval: { text: t("onboarding.documents.statuses.awaitingApproval"), variant: "info", Icon: IconRefresh },
});

export default function DocumentRow({ document, onUpload, onRefresh }) {
    const { t } = useTranslation("banking");
    const [docLoading, setDocLoading] = useState(false);
    const [showRefresh, setShowRefresh] = useState(false);

    // `t` changes identity on a language switch, so the labels re-resolve.
    const docsLabels = useMemo(() => getDocsLabels(t), [t]);
    const statusConfig = useMemo(() => getStatusConfig(t), [t]);

    const config = statusConfig[document.status] || statusConfig.pending;
    const { Icon } = config;
    const canUpload = ['pending', 'rejected'].includes(document.status);

    async function handleUpload() {
        if (document.url) {
            window.open(document.url, '_blank');

            let refreshed = false;
            const cleanup = () => {
                window.document.removeEventListener('visibilitychange', onVisibilityChange);
                window.removeEventListener('focus', onFocus);
            };
            const triggerRefresh = () => {
                if (refreshed) return;
                refreshed = true;
                cleanup();
                onRefresh?.();
            };
            const onVisibilityChange = () => { if (!window.document.hidden) triggerRefresh(); };
            const onFocus = () => triggerRefresh();

            window.document.addEventListener('visibilitychange', onVisibilityChange);
            window.addEventListener('focus', onFocus);
            return;
        }
        setShowRefresh(true);
        await onUpload(document, setDocLoading);
        setShowRefresh(false);
    }

    async function handleRefresh() {
        setDocLoading(true);
        await onRefresh?.();
        setDocLoading(false);
        setShowRefresh(false);
    }

    return (
        <div className="document-row">
            <div className="document-row-left">
                <StatusChip
                    variant={config.variant}
                    text={config.text}
                    icon={
                        <Icon
                            className={document.status === 'awaiting_approval' ? 'analysis-spin' : ''}
                            size={16}
                        />
                    }
                />
            </div>

            <Text.h5 className="document-row-label" contrast={7}>
                {docsLabels[document.type?.toUpperCase()] || document.type}
            </Text.h5>

            <div className="document-row-actions">
                {docLoading && (
                    <Mui.CircularProgress size={18} thickness={5} sx={{ color: 'var(--primary-5)' }} />
                )}

                {canUpload && !docLoading && (
                    <Button
                        size="small"
                        color="primary"
                        endIcon={!document.url ? <IconPlus /> : undefined}
                        onClick={handleUpload}
                    >
                        {document.url ? t('onboarding.documents.access') : t('common:actions.send')}
                    </Button>
                )}

                {showRefresh && !docLoading && (
                    <Button
                        size="small"
                        color="default"
                        endIcon={<IconRefresh />}
                        onClick={handleRefresh}
                    >
                        {t('common:actions.refresh')}
                    </Button>
                )}
            </div>
        </div>
    );
}
